import { DirectoryNode, FileNode, mapNodeFromApi, type Node, type NodeApiResponse } from '../../types/admin/nodes';
import { AxiosInstance } from 'axios';
import { Result } from 'services/Result';
import { AdminImageType } from 'types/admin/media';

export class MediaLibraryService {
  constructor(private readonly axiosInstance: AxiosInstance) {
  }

  getNodes = async (): Promise<Node[]> => {
    const response = await this.axiosInstance.get<Result<NodeApiResponse[]>>('/api/admin/media-library');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data.map(mapNodeFromApi);
    }

    throw new Error('There was an internal error while fetching file manager nodes.');
  }

  getMedia = async (id: string | undefined): Promise<AdminImageType> => {
    if (!id) {
      throw new Error('There was an internal error while fetching media.');
    }

    const response = await this.axiosInstance.get<Result<AdminImageType>>(`/api/admin/media-library/${id}`);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }

    throw new Error('There was an internal error while fetching media.');
  }

  createDirectory = async (name: string, parentId: string): Promise<DirectoryNode> => {
    const response = await this.axiosInstance.post<Result<NodeApiResponse>>('/api/admin/media-library/directory', {
      directoryName: name,
      parentId,
    });

    if (response.data && !response.data.isError && response.data.data) {
      return mapNodeFromApi<DirectoryNode>(response.data.data);
    }

    throw new Error('There was an internal error while creating the directory.');
  }

  renameNode = async (id: string, newName: string): Promise<void> => {
    const response = await this.axiosInstance.put('/api/admin/media-library/rename', {
      nodeId: id,
      newName,
    });

    if (response.status !== 204) {
      throw new Error('There was an internal error while renaming the node.');
    }
  }

  moveNodes = async (nodeIds: string[], targetId: string): Promise<void> => {
    const response = await this.axiosInstance.put('/api/admin/media-library/move', {
      nodeIds,
      targetDirectoryId: targetId,
    });

    if (response.status !== 204) {
      throw new Error('There was an internal error while moving the nodes.');
    }
  }

  removeNodes = async (nodeIds: string[]): Promise<void> => {
    await this.axiosInstance.put('/api/admin/media-library/trash', {
    nodeIds });
  }

  uploadFile = async (file: File, parentId: string): Promise<FileNode> => {
    const response = await this.axiosInstance.post<Result<NodeApiResponse>>('/api/admin/media-library/upload', {
      data: file,
      parentId,
    }, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (response.data && !response.data.isError && response.data.data) {
      return mapNodeFromApi<FileNode>(response.data.data);
    }

    throw Error('There was an internal error while uploading this image.');
  }
}